import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import styled, { css, keyframes } from 'styled-components';
import { camelToDash } from '@codex/helpers';

import icons from '../index';

const SIZES = {
  NORMAL: 'normal',
  LARGE: 'large'
};

const spinAnim = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Wrapper = styled.span`
  display: inline-block;
  vertical-align: middle;
  fill: currentColor;
  margin: 0;
  width: 16px;
  height: 16px;
  stroke: currentColor;
  box-sizing: border-box;

  .dc-fill {
    fill: currentColor;
  }

  .dc-no-fill {
    fill: none;
  }

  .dc-stroke {
    stroke: currentColor;
  }

  .dc-no-stroke {
    stroke: none;
  }

  ${({
    icon,
    rotate,
    size,
    spin,
    visible
  }) => css`
    transform: rotate(${rotate}deg);

    ${!visible && css`
      visibility: hidden;
    `};

    ${spin && css`
      display: inline-block;
      animation: ${spinAnim} 1s infinite linear;
    `};

    ${size === SIZES.LARGE && css`
      width: 28px;
      height: 28px;
    `};

    ${icon === 'Spinner' && css`
      stroke-width: 8px;
    `};
  `};
`;

export class Icon extends React.PureComponent {

  element = React.createRef();

  componentDidMount() {
    this.clearSize();
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps() {
    this.clearSize();
  }

  clearSize() {
    if (icons.clearSize && this.element.current) {
      const svgElements = this.element.current.querySelectorAll('svg');

      for (let i = 0; i < svgElements.length; i++) {
        svgElements[i].removeAttribute('width');
        svgElements[i].removeAttribute('height');
      }
    }
  }

  render() {
    const { props } = this;
    const IconComponent = icons[props.icon];
    if (IconComponent === undefined) {
      // eslint-disable-next-line no-console
      console.error(`Icon ${props.icon} is not available to use.`);
      return <noscript />;
    }

    return (
      <Wrapper
        {...props}
        className={cx(
          'dc-icon',
          `dc-icon-${camelToDash(props.icon)}`,
          {
            // Needed for BackstopJS tests
            'dc-spin': props.spin
          },
          props.className
        )}
        data-name={props.icon}
        ref={this.element}
        data-qa={props.qa}
      >
        <IconComponent />
      </Wrapper>
    );
  }
}

Icon.SIZES = SIZES;

Icon.propTypes = {
  className: PropTypes.string,
  icon: PropTypes.string.isRequired,
  rotate: PropTypes.number,
  size: PropTypes.oneOf(Object.values(Icon.SIZES)),
  spin: PropTypes.bool,
  visible: PropTypes.bool,
  qa: PropTypes.string
};

Icon.defaultProps = {
  className: undefined,
  rotate: 0,
  size: SIZES.NORMAL,
  spin: false,
  visible: true,
  qa: undefined
};

export default styled(Icon)``;
